import React from 'react';
import { PillarData } from './types';
import {
    getLatestPillarMonthYear,
    anchorReportingPeriodsToLatest
} from '../../../utils/reportingPeriod.ts';

// Icons for categories
const TargetIcon = (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
    </svg>
);

const ComparisonIcon = (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M8 17v-6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v6" />
    </svg>
);

const TrendIcon = (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M9 17l3-6 4 4 5-9" />
    </svg>
);

// Pillar-based data structure
const basePillarsData: PillarData[] = [
    {
        id: 'distribution',
        label: 'Distribution Power',
        categories: [
            {
                id: 'target-achievement',
                title: 'Target Achievement',
                description: 'Example questions about how we track goal attainment and identify gaps across OpCos.',
                iconColor: 'text-blue-600',
                bgColor: 'bg-blue-50',
                icon: TargetIcon,
                questions: [
                    'Which OpCos missed their Numeric Distribution targets in Q2 2025?',
                    'Highlight if any OpCo has a Numeric Distribution below 60% in August 2025.',
                    'Are we on track to hit the 2025 year-end target for Numeric Distribution in New Zealand?',
                    'Show me the gap to target for DC for Malaysia in Sep 2025.',
                ]
            },
            {
                id: 'comparisons-ranking',
                title: 'Comparisons & Ranking',
                description: 'Example questions about how we benchmark and rank performance across countries and regions.',
                iconColor: 'text-purple-600',
                bgColor: 'bg-purple-50',
                icon: ComparisonIcon,
                questions: [
                    'Rank all OpCos by Numeric Distribution % for June 2025 from highest to lowest.',
                    'Compare the Numeric Distribution performance between Indonesia and Vietnam for H1 2025.',
                    'Which country achieved the highest Numeric Distribution growth during Q3 2025?',
                    'How does Malaysia\'s Numeric Distribution rank against the top-performing OpCo in the region for Sep 2025?'
                ]
            },
            {
                id: 'growth-trends',
                title: 'Growth & Trends',
                description: 'Example questions about how we analyze historical performance and spot month-over-month changes.',
                iconColor: 'text-indigo-600',
                bgColor: 'bg-indigo-50',
                icon: TrendIcon,
                questions: [
                    'Flag any OpCos where Numeric Distribution declined month-over-month in Q2 2025.',
                    'Identify any Distribution KPIs for Myanmar showing a negative trend from Jan 2025 to July 2025.',
                    'What is the trend in Numeric Distribution for Malaysia over Jan 2025 - June 2025?',
                    'Has the Direct Coverage in MM improved since Jan 2025?'
                ]
            }
        ]
    },
    {
        id: 'transaction',
        label: 'Transaction Capture',
        categories: [
            {
                id: 'transaction-target',
                title: 'Target Achievement',
                description: 'Example questions about how we monitor transaction capture goals and performance gaps.',
                iconColor: 'text-blue-600',
                bgColor: 'bg-blue-50',
                icon: TargetIcon,
                questions: [
                    'Gap between forecasted and actual BTV for Philippines Oct 2025',
                    'Report on Active B2B target achievement for Philippines 2025/09',
                    'Show me the variance for HNK Sales Headcount coverage ratio for ID against the plan by Oct 2025',
                    'Did Philippines stay within the target for outlets per Distributor rep in Q1 2025'
                ]
            },
            {
                id: 'transaction-comparison',
                title: 'Comparisons & Ranking',
                description: 'Example questions about how we compare transaction capture across OpCos.',
                iconColor: 'text-purple-600',
                bgColor: 'bg-purple-50',
                icon: ComparisonIcon,
                questions: [
                    'Top 3 countries for e-Business revenue share in Oct 2025',
                    'List countries with Active B2B Customers % above 50% in Asia Pacific Q3 2025',
                    'Lowest performing OpCo in BTV for 2025/10',
                    'Who handles more outlets per HNK rep TW or PH in Q1 2025'
                ]
            },
            {
                id: 'transaction-trends',
                title: 'Growth & Trends',
                description: 'Example questions about how we track transaction capture trends over time.',
                iconColor: 'text-indigo-600',
                bgColor: 'bg-indigo-50',
                icon: TrendIcon,
                questions: [
                    'Trend of monthly Active B2B Customers % for Papua New Guinea over Aug to Oct 2025',
                    'Is the number of outlets per Distributor rep increasing (monthly trend) in Vietnam in Q3 2025',
                    'Plot the trajectory of outlets per Heineken rep for Korea On Trade and Korea Off trade since Jan to Oct 2025',
                    'Analyze whether the monthly TFE of Net Revenue (%) in Vietnam increased in October 2025 compared to the Q3 2025 average.'
                ]
            }
        ]
    },
    {
        id: 'execution',
        label: 'Execution Excellence',
        categories: [
            {
                id: 'execution-target',
                title: 'Target Achievement',
                description: 'Example questions about how we track execution excellence goals and gaps.',
                iconColor: 'text-blue-600',
                bgColor: 'bg-blue-50',
                icon: TargetIcon,
                questions: [
                    'Target variance for No. of SKUs per ASO in New Caledonia Q1 2025.',
                    'Which OpCos missed the Call Compliance target in Q2 2025?',
                    'How far off was New Zealand from their PICOS Availability % plan in Q3 2024?'
                ]
            },
            {
                id: 'execution-comparison',
                title: 'Comparisons & Ranking',
                description: 'Example questions about how we benchmark execution excellence across OpCos.',
                iconColor: 'text-purple-600',
                bgColor: 'bg-purple-50',
                icon: ComparisonIcon,
                questions: [
                    'Rank SG, MY, and PH by SKUs per Active Selling Outlet for first 6 months of 2025',
                    'Show me a side-by-side of Call Compliance % for Korean On-trade versus Off-trade in July 2025.',
                    'Who has the better PICOS Score: Malaysia or Indonesia in Oct 2025'
                ]
            },
            {
                id: 'execution-trends',
                title: 'Growth & Trends',
                description: 'Example questions about how we analyze execution excellence trends.',
                iconColor: 'text-indigo-600',
                bgColor: 'bg-indigo-50',
                icon: TrendIcon,
                questions: [
                    'Show me Jan to Oct 2025 trajectory for PICOS Availability % in Singapore.',
                    'Call Compliance % trend in Malaysia over Q1, Q2 and Q3 2025',
                    'Vietnam SKU/ASO growth rate Q1 vs Q2 2025.',
                    'Comment upon KPIs in Execution Excellence trending (monthly) for VN between Jan and July 2025?'
                ]
            }
        ]
    },
    {
        id: 'loyalty',
        label: 'Customer Loyalty',
        categories: [
            {
                id: 'loyalty-target',
                title: 'Target Achievement',
                description: 'Example questions about how we monitor customer loyalty goals and performance.',
                iconColor: 'text-blue-600',
                bgColor: 'bg-blue-50',
                icon: TargetIcon,
                questions: [
                    'Did ID hit the NPS Score target in Sep 2025?',
                    'What is the NPS Value Coverage vs Target for Vietnam (VN) in Q2 2025?',
                    'Did NC meet the survey response target for 2025/08?'

                ]
            },
            {
                id: 'loyalty-comparison',
                title: 'Comparisons & Ranking',
                description: 'Example questions about how we compare customer loyalty across OpCos.',
                iconColor: 'text-purple-600',
                bgColor: 'bg-purple-50',
                icon: ComparisonIcon,
                questions: [
                    'Which OpCo had the lowest Customer Loyalty score (NPS Score %) in full year 2024?',
                    'List the bottom 3 markets in terms of NPS Value Coverage (% of Rev) for Q1 2025.',
                    'Which markets achieved more than 80% NPS Value Coverage in Q3 2025?',
                    'List the top 5 countries with the highest engagement (NPS Response Rate) in Q3 2025.'

                ]
            },
            {
                id: 'loyalty-trends',
                title: 'Growth & Trends',
                description: 'Example questions about how we track customer loyalty trends over time.',
                iconColor: 'text-indigo-600',
                bgColor: 'bg-indigo-50',
                icon: TrendIcon,
                questions: [
                    'Has the NPS Value Coverage improved Year-over-Year for Q1, Q2, Q3 for Myanmar (MM)?',
                    'What is the YoY change in NPS Score % for PG in Q3 2025?',
                    'Plot the evolution of NPS survey response rate for Vietnam (VN) from 2025-01 to 2025-06.',
                    'Has the NPS Response Rate stabilized for NZ in the second half of 2025 (monthly trend)?'

                ]
            }
        ]
    }
];

const latestPillarMonthYear = getLatestPillarMonthYear();

export const pillarsData: PillarData[] = basePillarsData.map(pillar => ({
    ...pillar,
    categories: pillar.categories.map(category => ({
        ...category,
        questions: category.questions.map(q => anchorReportingPeriodsToLatest(q, latestPillarMonthYear))
    }))
}));

// Helper to get categories for a specific pillar
export const getCategoriesByPillar = (pillarId: string) => {
    const pillar = pillarsData.find(p => p.id === pillarId);
    return pillar?.categories || [];
};

// Default pillar
export const DEFAULT_PILLAR_ID = 'distribution';
